import { config as sharedConfig } from "@moe/priv/config";
import { Character } from "@moe/priv/model/character";
import { CandidateMessage, CharacterMessage, SingleMessage, UserMessage } from "@moe/priv/model/message";
import type { GenerationData, GenerationError, RegenerationData } from "@moe/priv/types/types";
import { useQueryClient } from "@tanstack/react-query";
import { useAPISettings } from "@web/hooks/use-api-settings";
import { useEscape } from "@web/hooks/use-escape";
import { config } from "@web/lib/config";
import { useDataContext } from "@web/route-services/chat/data-context";
import { HistoryCache } from "@web/route-services/chat/history-cache";
import { useChatAnalytics } from "@web/route-services/chat/use-chat-analytics";
import { useRenderTextChunk } from "@web/route-services/chat/use-render-text-chunk";
import { useAppContext } from "@web/route-services/root/AppContext";
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";

const generationTimeout = 40000;

interface Props {
  chatID: number;
  cache: HistoryCache;
  historyRef: React.MutableRefObject<Element | null>;
}

interface CreateClientParams {
  path: string;
  params: object;
}

export type ChatService = ReturnType<typeof useChatService>["chatService"];
/**
 * Manages the mutation of chat history.
 *
 * @param chatID - The ID of the chat.
 * @param cache - The react query cache for the chat history.
 */
export const useChatService = ({ chatID, cache, historyRef }: Props) => {
  const [isGenerating, setIsGenerating] = useState(false);
  // The base ID of the message being edited, i.e the "user" or "base" kind
  const [editingMessageID, setEditingMessageID] = useState<number | undefined>();
  const { access, session, createDialog } = useAppContext();
  const { persona, chars } = useDataContext();
  const timeoutIDRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const flagRef = useRef({ sent: false, fetchedPreviousPage: false, replied: false, newChunk: false });
  const queryClient = useQueryClient();
  const abortControllerRef = useRef<AbortController>(new AbortController());
  const { renderTextChunk } = useRenderTextChunk({ cache });
  const { settings } = useAppContext();
  const analytics = useChatAnalytics();
  
  // Refs to track generation data for analytics
  const generationDataRef = useRef<{
    startTime: number;
    characterId?: number;
    text: string;
  }>({ startTime: 0, text: "" });

  // We use this to determine if the bottom message has changed
  const bottomCandidateIDRef = useRef<number | undefined>(undefined);

  /**
   * Represent the array index for the bottom character messsage in the history.
   * If the bottom message is not a character message, this is undefined.
   */
  const [bottomCandidateIDX, setBottomCandidateIDX] = useState<number | undefined>();

  /**
   * Synchronizes the candidateIDX state with the bottom character message
   */
  useEffect(() => {
    const unsubscribe = queryClient.getQueryCache().subscribe((event) => {
      const keys = event.query.queryKey;
      // Ignore all other queries except the chat history query for this chat
      if (keys[0] !== "chatHistory" || keys[1] !== chatID) return;

      const last = cache.getLast();

      // Set to undefined for non character messages
      if (!Array.isArray(last)) {
        setBottomCandidateIDX(undefined);
        return;
      }

      // If the bottom message has not changed ignore
      if (bottomCandidateIDRef.current === last?.[0].id) return;

      // Keep the bottomMessageIDRef up to date
      bottomCandidateIDRef.current = last?.[0].id;

      // When the bottom character message changes, reset the candidate index to that of the new bottom message
      const initialCandidateIDX = last.findIndex((m) => m.isChosen) || 0;
      setBottomCandidateIDX(initialCandidateIDX);
      return () => {
        unsubscribe();
      };
    });
  }, [cache, chatID, queryClient]);

  const apiSettings = useAPISettings();

  // Stop editing when escape key is pressed
  useEscape(() => {
    setEditingMessageID(undefined);
  });

  const setNewChunkFlag = () => {
    flagRef.current.newChunk = true;
  };

  const resetGenerationTimeout = useCallback(() => {
    if (timeoutIDRef.current) clearTimeout(timeoutIDRef.current);
    timeoutIDRef.current = setTimeout(() => {
      // Track timeout error
      analytics.trackGenerationError(chatID, "timeout", "Generation timeout exceeded");
      abortControllerRef.current?.abort();
    }, generationTimeout);
  }, [analytics, chatID]);

  /**
   * Creates a new EventSource client for the chat generation API.
   *
   * @param path - The path to the chat generation API endpoint.
   * @param params - The parameters to include in the API request.
   * @returns A new EventSource client instance.
   */
  const createClient = ({ path, params }: CreateClientParams) => {
    if (!session) throw new Error("No session found while attempting to use chat service");
    setIsGenerating(true);
    // Clear old timeout
    clearTimeout(timeoutIDRef.current);
    
    // Reset generation tracking data
    generationDataRef.current = {
      startTime: Date.now(),
      characterId: undefined,
      text: ""
    };

    const processed = {
      ...params,
      provider: {
        kind: apiSettings.provider,
        baseURL: apiSettings.baseURL,
        apiKey: apiSettings.apiKey,
        model: apiSettings.model,
        customInstructions: apiSettings.customInstructions,
        maxContextTokens: apiSettings.maxContextTokens
      }
    };

    const jwt = session.access_token;
    const url = new URL(config.apiURL + path);
    url.searchParams.append("connectionParams", JSON.stringify({ token: jwt }));
    url.searchParams.append("params", JSON.stringify(processed));

    const client = new EventSource(url);
    const controller = abortControllerRef.current;

    // Abort signal for manual abort
    controller.signal.addEventListener("abort", () => {
      client.close();
      setIsGenerating(false);
    });

    // Start the initial inactivity timer
    resetGenerationTimeout();

    // Handle error while establishing SSE connection
    client.onerror = (event) => {
      toast.error("Error while connecting to chat service, sorry :< maybe we're down?");
      console.error("Error while establishing SSE connection", event);
      // Track connection error
      analytics.trackGenerationError(chatID, "connection", "SSE connection error");
      client.close();
      setIsGenerating(false);
    };

    client.addEventListener("done", () => {
      setIsGenerating(false);
      client.close();
      abortControllerRef.current = new AbortController();
      
      // Track generation completed
      const { startTime, characterId, text } = generationDataRef.current;
      if (characterId && text.length > 0) {
        const generationDurationMs = Date.now() - startTime;
        analytics.trackGenerationCompleted(
          chatID,
          characterId,
          text.length,
          generationDurationMs,
          persona?.id
        );
      }
    });

    // Handle explicitly returned errors from /chat/generate
    client.addEventListener("sse-error", (event) => {
      let error: GenerationError;
      try {
        error = JSON.parse(event.data);
      } catch (e) {
        toast.error("An unknown error occurred while generating a response.");
        console.error("Error parsing error event data:", e);
        // Track unknown error
        analytics.trackGenerationError(
          chatID,
          "unknown",
          "Error parsing error event data"
        );
        return;
      }
      
      // Track generation error
      let errorType: string;
      if (error.kind === "rate-limit") {
        errorType = "rate-limit";
        toast.error(
          `You reached the ratelimit set to deter bots.. I am impressed. If you're not a bot please ping @cyan, I'll reset the limit for you. Or you could wait ~${sharedConfig.usageResetIntervalHours}h for this to reset.`
        );
      } else if (error.message.toLowerCase().includes("timeout")) {
        errorType = "timeout";
        toast.error(error.message);
      } else if (error.message.toLowerCase().includes("connection")) {
        errorType = "connection";
        toast.error(error.message);
      } else {
        errorType = "unknown";
        toast.error(error.message);
      }
      
      analytics.trackGenerationError(chatID, errorType, error.message);
      
      console.error("SSE error:", event);
      client.close();
      setIsGenerating(false);
    });

    return client;
  };

  /**
   * Get a random character from the list of characters in the chat.
   */
  const getRandomChar = (): Character | undefined => {
    const values = Array.from(chars.values());
    return values[Math.floor(Math.random() * values.length)];
  };

  /**
   * Generate a character reply and stream the response to the chat history.
   * If no character is specified, a random character is chosen.
   * @param charID - The ID of the character to use when generating the reply.
   */
  const generate = async (charID: number) => {
    if (isGenerating) return;

    const client = createClient({
      path: "/chat/generate",
      params: { kind: "generate", chatID, personaID: persona.id, characterID: charID }
    });

    client.onmessage = (event) => {
      let data: GenerationData;
      resetGenerationTimeout();
      try {
        data = JSON.parse(event.data);
      } catch (e) {
        console.error("Error parsing event data:", e);
        return;
      }
      // Set the new chunk flag so we could properly manage scroll position
      setNewChunkFlag();

      // TODO: refactor scroll management
      let target = cache.getByID(data.messageID);
      if (!target) {
        // Add empty message to cache if it doesn't exist
        const newMessage = [
          {
            id: data.messageID,
            kind: "base" as const,
            text: "",
            characterID: data.characterID,
            isChosen: true,
            createdAt: new Date().toISOString()
          }
        ] as CharacterMessage;
        cache.push(newMessage);
        target = newMessage;
        // Set replied flag so we could properly manage scroll position
        flagRef.current.replied = true;
        cache.replace(data.messageID, target);
      }
      
      const chunkContent = data.chunk.choices[0].delta.content || "";
      generationDataRef.current.text += chunkContent;
      generationDataRef.current.characterId = data.characterID;
      
      renderTextChunk({
        content: chunkContent,
        kind: "generation",
        messageID: data.messageID
      });
    };
  };

  /**
   * Regenerate an existing character response.
   *
   * @param id - The ID of the message to be regenerated.
   * @param charID - The ID of the character to use when regenerating the response.
   * @returns - A promise that resolves when the regeneration is complete.
   */
  const regenerate = async (id: number) => {
    if (isGenerating) return;
    const message = cache.getByID(id);
    if (!message) throw new Error("Message to be regenerated not found in cache.");
    if (!Array.isArray(message)) throw new Error("Expected target message to be a character message");
    const characterID = message[0].characterID;
    if (!characterID) return;
    
    // Track regeneration count
    const regenerationCount = message.filter(m => m.kind === "candidate").length + 1;

    const client = createClient({
      path: "/chat/generate",
      params: {
        kind: "regenerate",
        chatID,
        targetMessageID: id,
        personaID: persona.id,
        characterID
      }
    });
    client.onmessage = (event) => {
      let data: RegenerationData;
      resetGenerationTimeout();
      try {
        data = JSON.parse(event.data);
      } catch (e) {
        console.error("Error parsing event data:", e);
        return;
      }
      // Set the new chunk flag so we could properly manage scroll position
      setNewChunkFlag();

      const target = cache.getByID(data.messageID);
      if (!target)
        throw new Error("Error while processing regeneration chunk. Base messageID does not exist in cache.");
      if (!Array.isArray(target)) throw new Error("Expected target message to be a character message");

      const candidates = target.filter((m) => m.kind === "candidate");
      // If candidate message doesn't exist on the target message, add it
      let targetCandidate = candidates.find((m) => m.id === data.candidateID);
      if (!targetCandidate) {
        const newCandidate = {
          id: data.candidateID,
          kind: "candidate" as const,
          text: "",
          characterID: data.characterID,
          isChosen: true,
          createdAt: new Date().toISOString()
        } as CandidateMessage;

        // Set all other candidates to not chosen
        target.forEach((c) => {
          c.isChosen = false;
        });
        target.push(newCandidate);
        targetCandidate = newCandidate;

        // Immediately switch to the regenerated candidate
        setBottomCandidateIDX(target.length - 1);
        cache.replace(data.messageID, target);
        
        // Track message regenerated event
        analytics.trackMessageRegenerated(
          chatID,
          data.characterID,
          data.messageID,
          target.filter(m => m.kind === "candidate").length
        );
      }
      
      const chunkContent = data.chunk.choices[0].delta.content || "";
      generationDataRef.current.text += chunkContent;
      generationDataRef.current.characterId = data.characterID;
      
      renderTextChunk({
        kind: "regeneration",
        messageID: data.messageID,
        candidateID: data.candidateID,
        content: chunkContent
      });
    };
  };

  /**
   * Continue the conversation by generating a character response.
   */
  const continue_ = async () => {
    const charID = getRandomChar()?.id;
    if (!charID) return;
    generate(charID);
  };

  /**
   * Sends a user message to the chat and initiates message generation.
   *
   * @param text - The text of the user's message to send.
   * @returns A promise that resolves when the message has been sent and generation has started.
   */
  const send = async (text: string) => {
    if (isGenerating) return;

    const id = await access.message.create({ chatID, text });
    const userMessage = {
      id,
      kind: "user",
      text,
      createdAt: new Date().toISOString()
    } as UserMessage;
    cache.push(userMessage);

    // If there's only a single character, then we generate a response using that character
    // If there are multiple characters, we do nothing.
    // In the multiple characters case we let the user explicitly choose a character instead
    if (chars.size === 1) {
      const id = Array.from(chars.values())[0].id;
      generate(id);
      flagRef.current.sent = true;
    }
  };

  /**
   * Rewinds the chat history by removing all messages after the specified message ID.
   *
   * @param id - The ID of the message to rewind to.
   * @returns A promise that resolves when the rewind operation is complete.
   */
  const rewind = async (id: number) => {
    const ids = cache.getAll().map((m) => (Array.isArray(m) ? m[0].id : m.id));
    const targets = ids.slice(ids.indexOf(id) + 1, ids.length);
    const messageCount = targets.length;

    const deleteMessages = async () => {
      await access.message.delete(targets);
      cache.delete(targets);
      
      // Track chat rewound event
      analytics.trackChatRewound(chatID, messageCount);
    };

    if (settings?.confirmDelete === true) {
      createDialog({
        title: "Rewind Chat",
        description: `Are you sure you want to rewind the chat history? This will delete ${messageCount} message${messageCount === 1 ? "" : "s"}.`,
        onAction: deleteMessages,
        actionLabel: "Rewind"
      });
      return;
    }
    deleteMessages();
  };

  /**
   * Deletes a message from the chat.
   *
   * @param id - The ID of the message to delete.
   * @returns A promise that resolves when the message has been deleted.
   */
  const _delete = async (id: number) => {
    const deleteMessage = async () => {
      // Get message type before deletion
      const message = cache.getByID(id);
      const messageType = message ? (Array.isArray(message) ? "character" : message.kind) : "unknown";
      
      await access.message.delete(id);
      cache.delete(id);
      
      // Track message deleted event
      analytics.trackMessageDeleted(
        chatID,
        id,
        messageType
      );
    };
    if (settings?.confirmDelete === true) {
      createDialog({
        title: "Delete Message",
        description: "Are you sure you want to delete this message?",
        onAction: deleteMessage,
        actionLabel: "Delete"
      });
      return;
    }
    deleteMessage();
  };

  /**
   * Initiates the editing process for a message.
   *
   * @param id - The ID of the message to edit.
   */
  const edit = async (id: number) => {
    const message = cache.getByID(id);
    if (!message) {
      console.error(`Message with id ${id} not found`);
      return;
    }
    const m = Array.isArray(message) ? message[0] : message;
    setEditingMessageID(m.id);
  };

  /**
   * Submits an edited message.
   *
   * @param message - The original message object.
   * @param editText - The new text for the edited message.
   * @returns A promise that resolves when the edit has been submitted.
   */
  const submitEdit = async (message: SingleMessage, editText: string) => {
    await access.message.update({
      kind: message.kind,
      id: message.id,
      text: editText
    });
    cache.invalidate();
    setEditingMessageID(undefined);
    
    // Track message edited event
    analytics.trackMessageEdited(
      chatID,
      message.id,
      message.kind,
      persona?.id
    );
  };

  const cancelEdit = () => {
    setEditingMessageID(undefined);
  };

  /**
   * Sets a candidate message as the chosen message for a character base message.
   *
   * @param baseID - The ID of the base message.
   * @param candidateID - The ID of the candidate message to set as chosen,
   * if this is not provided the base message will be set as chosen.
   * @returns A promise that resolves when the chosen message has been set.
   */
  const setChosen = async (baseID: number, candidateID?: number) => {
    // Sync local state
    const message = cache.getByID(baseID);
    if (!message || !Array.isArray(message)) return;
    message.forEach((m) => {
      if (candidateID) {
        // Set the specified candidate as chosen
        m.isChosen = m.id === candidateID;
      } else {
        // Set the base message as chosen
        m.isChosen = m.kind === "base";
      }
    });
    cache.replace(baseID, message);
    await access.message.setChosen(baseID, candidateID);
  };

  const switchLeft = () => {
    // Ignore if last message isn't a character message
    const last = cache.getLast();
    if (!Array.isArray(last)) return;
    if (bottomCandidateIDX === undefined) return;
    const historyLength = cache.length();
    // Ignore if there's are no messages
    if (historyLength === 0) return;

    const newIDX = Math.max(bottomCandidateIDX - 1, 0);
    // If we're switching to a different candidate, sync this selection with the DB
    if (bottomCandidateIDX !== newIDX) {
      if (!last?.[0]?.id) return;
      const candidateID = last[newIDX].kind === "candidate" ? last[newIDX].id : undefined;
      setChosen(last[0].id, candidateID);
    }

    setBottomCandidateIDX(newIDX);

    // Scroll to the bottom
    if (historyRef.current) historyRef.current.scrollTop = historyRef.current.scrollHeight;
  };

  const switchRight = () => {
    // Ignore if last message isn't a character message
    const last = cache.getLast();
    if (!Array.isArray(last)) return;
    if (bottomCandidateIDX === undefined) return;
    const historyLength = cache.length();
    // Ignore if we're on the greeting message or if there are no message
    if (historyLength === 0) return;

    // We don't -1 from last.length, we allow going one over,
    // because the last index is a loading skeleton
    const max = last.length;
    console.log("max", max);
    console.log("bottomCandidateIDX", bottomCandidateIDX);
    const newIDX = Math.min(bottomCandidateIDX + 1, max);

    // If we're on the last real message and we're already isGenerating
    // Don't switch right
    if (bottomCandidateIDX === last.length - 1 && isGenerating) return;

    // If we're on the last real message and it's the only message
    // This is the greeting, which we don't want to regen
    if (bottomCandidateIDX === last.length - 1 && historyLength === 1) return;

    // If we're on the last real message and we're not already generating
    // And we're not on the greeting message
    // Regenerate the last message
    if (bottomCandidateIDX === last.length - 1 && !isGenerating) {
      if (!last?.[0]?.id) return;
      regenerate(last[0].id);
    }

    // If we're switching to a different candidate, sync this selection with the DB
    if (newIDX !== bottomCandidateIDX) {
      if (!last?.[0]?.id) return;
      const candidateID = last[newIDX]?.id;
      // The candidateID only exists if we're not switching to the skeleton
      if (candidateID) setChosen(last[0].id, candidateID);
    }

    setBottomCandidateIDX(newIDX);

    // Scroll to the bottom
    if (historyRef.current) {
      historyRef.current.scrollTop = historyRef.current.scrollHeight;
    }
  };

  /**
   * Aborts the current generation process.
   * A generation is anything that involves the chat service generating a response.
   * I.e generate, regenerate, continue.
   */
  const abort = async () => {
    const controller = abortControllerRef.current;
    if (controller) controller.abort();
  };

  return {
    isGenerating,
    editingMessageID,
    bottomCandidateIDX,
    chatService: {
      send,
      generate,
      continue: continue_,
      regenerate,
      rewind,
      delete: _delete,
      edit,
      submitEdit,
      setChosen,
      abort,
      cancelEdit,
      switchLeft,
      switchRight
    },
    flagRef
  };
};
